import { create, protoInt64, type JsonObject } from "@bufbuild/protobuf";
import { PaginationArgumentsSchema, type PaginationArguments, type PaginationPageInfo } from "#gen/common/v1/pagination_pb.js";
import type { PostalAddress } from "#gen/google/type/postal_address_pb.js";
import type { Checkout, Integration, LineItem, Order, Payment, PaymentInfo, Product } from "#gen/products/v1/product_pb.js";
import type { RecommendationType } from "#gen/products/v1/service_pb.js";
import {
	BeginCheckoutRequestSchema,
	CompleteCheckoutRequestSchema,
	CreateApplePaySessionRequestSchema,
	CreateCartPermalinkRequestSchema,
	GetCheckoutRequestSchema,
	GetPaymentRequestSchema,
	GetRecommendationsRequestSchema,
	IntegrationFilterSchema,
	ListIntegrationsRequestSchema,
	ListProductsRequestSchema,
	ProductFilterSchema,
	ProductService,
	UpdateCheckoutRequestSchema,
} from "#gen/products/v1/service_pb.js";
import { createClient, type ClientOptions } from "#lib/api/api.js";
import { handleError } from "#lib/api/error.js";
import { PUBLIC_URLS } from "#lib/url.js";
import type { FieldMask } from "@bufbuild/protobuf/wkt";
import type { CallOptions, Client } from "@connectrpc/connect";

export interface IntegrationList {
	integrations: Integration[];
	pageInfo?: PaginationPageInfo;
}

export class ProductClient {
	private client: Client<typeof ProductService>;

	constructor(options?: () => ClientOptions) {
		this.client = createClient(PUBLIC_URLS.products || "/", ProductService, options);
	}

	async listIntegrations(orgId: string, filter?: string, pagination?: PaginationArguments, options?: CallOptions): Promise<IntegrationList> {
		try {
			const res = await this.client.listIntegrations(
				create(ListIntegrationsRequestSchema, {
					orgId: orgId,
					filter: filter
						? create(IntegrationFilterSchema, {
								name: filter,
							})
						: undefined,
					pagination: pagination,
				}),
				options,
			);
			return res;
		} catch (e) {
			throw handleError(e);
		}
	}

	async listProducts(integrationId: string, filter?: string, limit: number = 10): Promise<Product[]> {
		try {
			const res = await this.client.listProducts(
				create(ListProductsRequestSchema, {
					integrationId: integrationId,
					filter: create(ProductFilterSchema, {
						title: filter,
					}),
					pagination: create(PaginationArgumentsSchema, {
						first: limit,
					}),
				}),
			);
			return res.products;
		} catch (e) {
			throw handleError(e);
		}
	}

	async getRecommendations(integrationId: string, id: string, recommendationType: RecommendationType): Promise<Product[]> {
		try {
			const res = await this.client.getRecommendations(
				create(GetRecommendationsRequestSchema, {
					integrationId: integrationId,
					id: id,
					recommendationType: recommendationType,
				}),
			);
			return res.products;
		} catch (e) {
			throw handleError(e);
		}
	}

	async getCheckout(integrationId: string, id: string): Promise<Checkout> {
		try {
			const res = await this.client.getCheckout(
				create(GetCheckoutRequestSchema, {
					integrationId: integrationId,
					id: id,
				}),
			);
			return res.checkout!;
		} catch (e) {
			throw handleError(e);
		}
	}

	async getPayment(integrationId: string, id: string): Promise<Payment> {
		try {
			const res = await this.client.getPayment(
				create(GetPaymentRequestSchema, {
					integrationId: integrationId,
					id: id,
				}),
			);
			return res.payment!;
		} catch (e) {
			throw handleError(e);
		}
	}

	async beginCheckout(integrationId: string, address: PostalAddress, items: LineItem[], requiresShipping: boolean): Promise<Checkout> {
		try {
			const res = await this.client.beginCheckout(
				create(BeginCheckoutRequestSchema, {
					integrationId: integrationId,
					lineItems: items,
					countryCode: address.regionCode,
					shippingAddress: address,
					requiresShipping: requiresShipping,
				}),
			);
			return res.checkout!;
		} catch (e) {
			throw handleError(e);
		}
	}

	async updateCheckout(checkout: Checkout, mask: FieldMask): Promise<Checkout> {
		try {
			const res = await this.client.updateCheckout(
				create(UpdateCheckoutRequestSchema, {
					checkout: checkout,
					mask: mask,
				}),
			);
			return res.checkout!;
		} catch (e) {
			throw handleError(e);
		}
	}

	async completeCheckout(checkout: Checkout, payment: PaymentInfo): Promise<Order> {
		try {
			const res = await this.client.completeCheckout(
				create(CompleteCheckoutRequestSchema, {
					checkout: checkout,
					paymentInfo: payment,
				}),
			);
			return res.order!;
		} catch (e) {
			throw handleError(e);
		}
	}

	async createApplePaySession(integration: Integration, validationURL: string): Promise<JsonObject> {
		try {
			const res = await this.client.createApplePaySession(
				create(CreateApplePaySessionRequestSchema, {
					integrationId: integration.id,
					validationUrl: validationURL,
				}),
			);
			return res.session!;
		} catch (e) {
			throw handleError(e);
		}
	}

	async createCartPermalink(integration: Integration, variantId: string, quantity: number, attributes: Record<string, string> = {}): Promise<string> {
		try {
			const res = await this.client.createCartPermalink(
				create(CreateCartPermalinkRequestSchema, {
					integrationId: integration.id,
					variantId: variantId,
					quantity: protoInt64.parse(quantity),
					attributes: attributes,
				}),
			);
			return res.url!;
		} catch (e) {
			throw handleError(e);
		}
	}
}
